/**
 * Custom hook for fetching and managing IA Corner content (reels).
 * Handles GraphQL queries for IA Corner reels with horizontal and vertical pagination,
 * automatic population of rails with insufficient items, and state management for loading and data.
 */

import Direction from '@shared/types/enums/direction';
import Main from '@shared/types/enums/main';
import Rail from '@shared/classes/rail';
import info from '@shared/api/graphQL/iaCorners/info';
import parser from '@shared/api/graphQL/parser';
import query from '@shared/api/graphQL/iaCorners/contentQuery';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError, useApolloClient, useQuery } from '@apollo/client';
import { IReelResponse } from '@shared/types/interfaces/rail';
import { SectionQueryParams } from '@shared/types/interfaces/query';
import { routerSelector } from '@shared/store/slices/router.slice';
import { useAppSelector } from '@shared/store/hooks';

// Constants
/** Minimum number of items required in a reel before auto-population stops */
const MIN_ITEMS = 10;
/** Number of items to fetch per horizontal page */
const HORIZONTAL_REEL_PAGE_SIZE = 30;
/** Number of reels to fetch per vertical page */
const VERTICAL_REEL_PAGE_SIZE = 10;

/**
 * This interface defines the properties a graphql query request can receive
 */
interface Props {
  id: string;
  ready?: boolean;
  customPageIndex?: {
    horizontal?: number;
    vertical?: number;
  };
  customPageSize?: {
    horizontal?: number;
    vertical?: number;
  };
}

/**
 * Section hook. Fetches and returns reels data.
 */
export default function useIACornerContent({
  id,
  ready = true,
  customPageIndex,
  customPageSize,
}: Props): {
  /**
   * Whether the request is complete or not
   */
  complete: boolean;
  /**
   * Obtained reels array
   */
  reels: Rail[];
  /**
   * Horizontal and vertical pagination callback
   */
  onPagination: (type: Main, direction: Direction) => void;
} {
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);
  const [complete, setComplete] = useState(
    !!currentParams?.rails || !!currentParams?.sliders || false
  );
  const [reels, setReels] = useState(
    (currentParams as { rails: Rail[] })?.rails || []
  );
  const client = useApolloClient();
  const pagination = useRef({
    horizontalPages: new Map<number, number>(),
    verticalPage: 0,
    totalVerticalPages: 0,
  });
  const populated = useRef<number[]>([]);

  // Memoized params

  const params = useMemo(() => {
    const params = {
      horizontalParams: {
        page: customPageIndex?.horizontal || 0,
        pageSize: customPageSize?.horizontal || HORIZONTAL_REEL_PAGE_SIZE,
      },
      verticalParams: {
        page: customPageIndex?.vertical || 0,
        pageSize: customPageSize?.vertical || VERTICAL_REEL_PAGE_SIZE,
      },
    };

    return params;
  }, [customPageIndex, customPageSize]);

  // Event handlers

  /**
   * Request completion callback.
   */
  const onCompleted = useCallback(() => {
    // Complete request
    setComplete(true);
  }, []);

  /**
   * Request error callback.
   */
  const onError = useCallback((error: ApolloError) => {
    // Print error
    console.error(error);
    // Complete request
    setComplete(true);
  }, []);

  /**
   * Handles horizontal and vertical pagination for the specified content type.
   * Updates query variables to trigger data fetching in the desired direction.
   *
   * @param {Main} type - The specific content type to paginate.
   * @param {Direction} direction - The pagination direction.
   */
  const onPagination = useCallback(
    (_type: Main, direction?: Direction, rail?: Rail) => {
      /**
       * Set query variables so that new data is fetched
       */
      let newVariables: SectionQueryParams = {};

      // Horizontal reel displacement
      if (direction === Direction.HORIZONTAL && rail?.pagination) {
        const currentPage = rail.pagination.currentPage;
        const nextPage = currentPage + 1;
        if (nextPage < rail.pagination.totalPages) {
          newVariables = {
            reelsHorizontalParams: {
              page: nextPage,
              pageSize: customPageSize?.horizontal || HORIZONTAL_REEL_PAGE_SIZE,
            },
            reelsVerticalParams: {
              page: pagination.current.verticalPage,
              pageSize: customPageSize?.vertical || VERTICAL_REEL_PAGE_SIZE,
            },
            reelVerticalIndex: rail.pagination.verticalIndex,
          };

          // Save new horizontal page for parser
          rail.pagination.currentPage = nextPage;
          pagination.current.horizontalPages.set(
            rail.pagination.verticalIndex,
            rail.pagination.currentPage
          );
        }
        // Halt callback
        else {
          return;
        }
      }
      // Vertical reel displacement
      else if (direction === Direction.VERTICAL) {
        const currentPage = pagination.current.verticalPage;
        const nextPage = currentPage + 1;
        const totalPages = pagination.current.totalVerticalPages;
        if (nextPage < totalPages) {
          pagination.current.verticalPage = nextPage;
          newVariables = {
            reelsHorizontalParams: {
              page:
                pagination.current.horizontalPages.get(
                  rail?.pagination?.verticalIndex || 0
                ) || 0,
              pageSize: customPageSize?.horizontal || HORIZONTAL_REEL_PAGE_SIZE,
            },
            reelsVerticalParams: {
              page: nextPage,
              pageSize: customPageSize?.vertical || VERTICAL_REEL_PAGE_SIZE,
            },
          };
        }
        // Halt callback
        else {
          return;
        }
      }

      /**
       * Fetches the next set of results for a paginated list field
       */
      const normalizedVariables = {
        horizontalParams: newVariables.reelsHorizontalParams,
        verticalParams: newVariables.reelsVerticalParams,
      };
      const data = client.readQuery({
        query,
        variables: normalizedVariables,
      });
      if (!data) {
        fetchMore({
          variables: normalizedVariables,
        });
      }
    },
    []
  );

  // Logic

  /**
   * Parses a given response and updates main contents
   */
  const updateContents = useCallback((response: { reels?: IReelResponse }) => {
    try {
      // Parse reels
      const reels = parser.reels(
        response?.reels,
        pagination.current.horizontalPages
      );
      if (reels) {
        // Update reels
        setReels((state) => {
          const reelData = (reels?.data || state).filter(
            (reel) => reel.data.length
          );
          return reelData;
        });

        // Update pagination data
        pagination.current.totalVerticalPages = response.reels?.totalPages || 0;

        // Populate reels if required
        const reelsToPopulate = reels?.data
          ?.map((reel) => {
            if (
              reel.pagination &&
              !populated.current.includes(reel.pagination.verticalIndex) &&
              reel.data.length < MIN_ITEMS &&
              (reel.pagination?.currentPage || 0) <
                (reel.pagination?.totalPages ||
                  reel?.pagination?.currentPage ||
                  0)
            ) {
              populated.current.push(reel.pagination.verticalIndex);
              return reel;
            }
          })
          .filter((items) => !!items);

        if (reelsToPopulate?.length) {
          reelsToPopulate.forEach((reel) => {
            onPagination(Main.RAILS, Direction.HORIZONTAL, reel);
          });
        }
      }
    } catch (e) {
      // Print error
      console.error(e);
    }
  }, []);

  /**
   * Fetch reels via graphql using the query hook
   */
  const { data, fetchMore } = useQuery(query, {
    skip: !ready,
    variables: {
      ...info(id),
      ...params,
    },
    onCompleted,
    onError,
  });

  /**
   * Updates data on change
   */
  useEffect(() => updateContents({ reels: data?.reels }), [data]);

  return {
    complete,
    reels,
    onPagination,
  };
}
